import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft, Clock, Plus, X } from '@/ui/icons'
import { useTrips } from '@/modules/trips'
import { useSubscription } from '@/modules/subscriptions/hooks/useSubscription'
import { useAppNav } from '@/contexts/AppNavContext'
import { RANGER_PANEL_WIDTH_CLASS, useRangerPanel } from '@/contexts/RangerPanelContext'
import type { RangerSessionSummary } from '@/shared/api/ranger'
import { cn } from '@/shared/utils'
import { useRanger } from '../hooks/useRanger'
import { useRangerSessions } from '../hooks/useRangerSessions'
import { RangerChat } from './RangerChat'
import { RangerHistory } from './RangerHistory'
import { RangerMark } from './RangerMark'

const ICON_BUTTON_CLASS =
  'inline-flex h-8 w-8 items-center justify-center rounded-md text-earth-600 transition-colors hover:bg-earth-100 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500'

/** A trip page is `/trips/:tripId`, one segment past `/trips`, and never the `new` placeholder. */
function useCurrentTripId(): string | undefined {
  const location = useLocation()
  const match = location.pathname.match(/^\/trips\/([^/]+)$/)
  if (!match || match[1] === 'new') return undefined
  return match[1]
}

/**
 * Ranger as a desktop column opened from the app nav, between the nav and the page. The app
 * shell keeps room for it, so the page moves over rather than sitting underneath. It's mounted
 * above the routes, so the conversation survives closing, reopening and changing pages.
 */
export function RangerPanel() {
  const { isOpen, closeRangerPanel } = useRangerPanel()
  const { expanded: navExpanded } = useAppNav()
  const navigate = useNavigate()
  const tripId = useCurrentTripId()
  const { trips } = useTrips()
  const ranger = useRanger(tripId)
  const { builtTrip, activeTool, messages, loading } = ranger
  const { status } = useSubscription()
  const canUseRanger = status?.features.canUseRanger ?? false

  const [focusRequest, setFocusRequest] = useState(0)

  useEffect(() => {
    if (isOpen) setFocusRequest((n) => n + 1)
  }, [isOpen])

  // Show the new trip as soon as it exists. The chat stays put for the invite step.
  const navigatedTripIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!builtTrip || navigatedTripIdRef.current === builtTrip.id) return
    navigatedTripIdRef.current = builtTrip.id
    navigate(`/trips/${builtTrip.id}`)
  }, [builtTrip, navigate])

  const [showHistory, setShowHistory] = useState(false)
  const [openingSessionId, setOpeningSessionId] = useState<string | null>(null)
  const [openFailed, setOpenFailed] = useState(false)
  // A chat older than the recent list still needs its name in the header once it's open.
  const [openedSession, setOpenedSession] = useState<{ runtimeSessionId: string; title: string | null } | null>(null)

  const closeHistory = () => {
    setShowHistory(false)
    setOpenFailed(false)
    setFocusRequest((n) => n + 1)
  }

  const startNewChat = () => {
    ranger.clearMessages()
    setOpeningSessionId(null)
    closeHistory()
  }

  const openSession = async (session: RangerSessionSummary) => {
    if (session.runtimeSessionId === ranger.sessionId) {
      closeHistory()
      return
    }

    setOpeningSessionId(session.id)
    setOpenFailed(false)
    try {
      const transcript = await ranger.openSession(session.id)
      if (!transcript) return

      setOpenedSession({ runtimeSessionId: transcript.runtimeSessionId, title: transcript.title })
      // Ranger acts on the open trip, so a chat about another trip has to bring that trip with it.
      // The trips list guards against a trip the user has since lost access to.
      if (transcript.tripId && transcript.tripId !== tripId && trips?.some((t) => t.id === transcript.tripId)) {
        navigate(`/trips/${transcript.tripId}`)
      }
      setOpeningSessionId(null)
      closeHistory()
    } catch {
      setOpeningSessionId(null)
      setOpenFailed(true)
    }
  }

  const handleRenamed = (session: RangerSessionSummary, title: string) => {
    if (openedSession?.runtimeSessionId === session.runtimeSessionId) {
      setOpenedSession({ runtimeSessionId: session.runtimeSessionId, title })
    }
  }

  const handleDeleted = (session: RangerSessionSummary) => {
    if (session.runtimeSessionId === ranger.sessionId) ranger.clearMessages()
  }

  // The list is the source of a chat's name, including one Ranger just named after its first reply.
  const recentSessions = useRangerSessions('recent', { enabled: isOpen && canUseRanger })
  const chatName =
    recentSessions.data?.pages[0]?.sessions.find((s) => s.runtimeSessionId === ranger.sessionId)?.title ??
    (openedSession?.runtimeSessionId === ranger.sessionId ? openedSession.title : null)

  const chatTitle =
    activeTool === 'build_trip' ? 'Build a trip' : chatName || (messages.length > 0 ? 'Ranger' : 'New chat')
  const tripName = tripId ? trips?.find((t) => t.id === tripId)?.name : undefined

  return (
    // Below the nav's layer, so the column slides out from behind it, and below modals and the
    // notifications panel, which should cover it like the rest of the page.
    <div
      className={cn(
        'fixed inset-y-0 z-30 hidden pointer-events-none md:block',
        navExpanded ? 'md:left-60' : 'md:left-14'
      )}
      aria-hidden={!isOpen}
    >
      <aside
        aria-label="Ranger AI Assistant"
        className={cn(
          'flex h-full flex-col border-r border-earth-200 bg-background transition-[transform,visibility] duration-200 ease-out',
          RANGER_PANEL_WIDTH_CLASS,
          isOpen ? 'pointer-events-auto translate-x-0' : 'invisible -translate-x-full'
        )}
        {...(!isOpen ? { inert: '' } : {})}
      >
        <div className={cn('flex h-14 flex-shrink-0 items-center justify-between gap-2 pr-2', showHistory ? 'pl-2' : 'pl-4')}>
          <div className="flex min-w-0 items-center gap-2">
            {showHistory ? (
              <button
                type="button"
                onClick={closeHistory}
                aria-label="Back to chat"
                title="Back to chat"
                className={ICON_BUTTON_CLASS}
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            ) : (
              <RangerMark thinking={loading} className="h-5 w-5 shrink-0 text-adventure-600" />
            )}
            <h2 className="truncate text-sm font-medium text-foreground" title={showHistory ? undefined : chatTitle}>
              {showHistory ? 'Chats' : chatTitle}
            </h2>
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            {canUseRanger && !showHistory && (
              <button
                type="button"
                onClick={() => setShowHistory(true)}
                aria-label="Chat history"
                title="Chat history"
                className={ICON_BUTTON_CLASS}
              >
                <Clock className="h-4 w-4" />
              </button>
            )}
            {(messages.length > 0 || showHistory) && (
              <button
                type="button"
                onClick={startNewChat}
                aria-label="Start a new chat"
                title="New chat"
                className={ICON_BUTTON_CLASS}
              >
                <Plus className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={closeRangerPanel}
              aria-label="Close Ranger AI Assistant"
              title="Close"
              className={ICON_BUTTON_CLASS}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="relative min-h-0 flex-1">
          {/* History sits over the chat, not in place of it, so a half-typed message and the
              scroll position are still there on the way back. */}
          <div className="h-full" {...(showHistory ? { inert: '' } : {})}>
            <RangerChat
              tripId={tripId}
              tripName={tripName}
              messages={messages}
              sendMessage={ranger.sendMessage}
              loading={ranger.loading}
              error={ranger.error}
              clearError={ranger.clearError}
              tools={{ activeTool, tripBuilt: builtTrip !== null, onStart: ranger.startTool, onStop: ranger.stopTool }}
              autoFocus={false}
              focusRequest={focusRequest}
            />
          </div>
          {showHistory && (
            <RangerHistory
              activeRuntimeSessionId={ranger.sessionId}
              openingSessionId={openingSessionId}
              openFailed={openFailed}
              onOpen={openSession}
              onRenamed={handleRenamed}
              onDeleted={handleDeleted}
              onClose={closeHistory}
            />
          )}
        </div>
      </aside>
    </div>
  )
}
